"use client";
import { axiosInstanceAuth } from "@components/axiosInstances";
import FormHelper from "@components/form/form";
import { Title, Button, Tabs, Text } from "@mantine/core";
import useAuthStore from "@store/authstore";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useTranslation } from "react-i18next";
import { useMutation } from "react-query";
import { useCustomNotification } from "utils/notifications/notifications";
import * as yup from "yup";
import {
  IconHelp,
  IconLogin2,
  IconNotebook,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import FingerprintJS from "@fingerprintjs/fingerprintjs";

const validationSchema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
});

// TODO: Separate this into a separate file
interface LoginCredentials {
  email: string;
  password: string;
  fingerprint?: string;
}

interface ProtectedFingerPrint {
  token: string;
  fingerprint: string;
}
interface LoginResponse {
  data: {
    token: string;
    user_id: string;
    name: string;
    email: string;
    company_name: string;
    company_date: string;
    division_permission: number;
    division_name: string;
    role: string;
    role_name: string;
    type_user: string;
    vendor_id: string;
    exp: number;
    status_partner?: number; // 1 = Partner List, 0 = Partner Candidate
    sap_vendor_code?: string;
    fingerprint?: string;
  };
}

interface ProtectedFingerprintResponse {
  data: {
    status: string;
    hash_fingerprint: string;
    token: string;
    token_expired: string;
  };
}

export const TabLogin = () => {
  const { t } = useTranslation("login");
  const router = useRouter();
  const setToken = useAuthStore((state) => state.setToken);
  const setUser = useAuthStore((state) => state.setUser);
  const setFingerprint = useAuthStore((state) => state.setFingerprint);
  const { notify } = useCustomNotification();
  const [activeTab, setActiveTab] = useState<string | null>("vendor");

  const loginMutation = useMutation<LoginResponse, unknown, LoginCredentials>(
    async (credentials: LoginCredentials) => {
      try {
        // TODO: Separate this into a separate file
        const response = await axiosInstanceAuth.post<LoginResponse>(
          "/api/auth/login-with-fingerprint",
          credentials
        );
        const {
          token,
          user_id,
          email,
          name,
          company_name,
          company_date,
          division_permission,
          role,
          role_name,
          type_user,
          vendor_id,
          exp,
          status_partner,
          sap_vendor_code,
          fingerprint,
          division_name,
        } = response.data.data;
        setToken(token);
        localStorage.setItem("fingerprint", fingerprint ?? "");
        setFingerprint(fingerprint ?? null);
        setUser({
          id: user_id,
          name: name,
          email: email,
          company_name: company_name,
          company_date: company_date,
          division_permission: division_permission,
          role: role,
          role_name: role_name,
          type_user: type_user,
          vendor_id: vendor_id,
          exp: exp,
          status_partner: status_partner,
          sap_vendor_code: sap_vendor_code,
          division_name: division_name,
        });

        return response.data;
      } catch (error: any) {
        throw error;
      }
    }
  );

  const protectedFingerPrintMutation = useMutation<
    ProtectedFingerprintResponse,
    unknown,
    ProtectedFingerPrint
  >({
    mutationFn: async (credentials: ProtectedFingerPrint) => {
      const response =
        await axiosInstanceAuth.post<ProtectedFingerprintResponse>(
          "/api/auth/protected",
          credentials
        );

      return response.data;
    },
    onError: (error) => {
      console.error("Error in protected fingerprint mutation:", error);
    },
  });

  // Baca ?redirect= dari URL — diisi oleh logout handler agar user
  // kembali ke halaman terakhir setelah berhasil login
  const searchParams = useSearchParams();
  const redirectParam = searchParams.get("redirect");

  const getRedirectUrl = (typeUser: string): string => {
    if (redirectParam) return redirectParam;
    return typeUser?.toLocaleLowerCase() === "internal"
      ? "/internal/dashboard"
      : "/vendor/home-page";
  };

  const DoLogin = async (credential: LoginCredentials) => {
    try {
      const fp = await FingerprintJS.load();
      const result = await fp.get();
      credential.fingerprint = result.visitorId;
      const resultLogin = await loginMutation.mutateAsync(credential);
      if (
        resultLogin.data.user_id !== "" &&
        resultLogin.data.user_id !== undefined &&
        resultLogin.data.user_id !== null
      ) {
        const resultProtected = await protectedFingerPrintMutation.mutateAsync({
          token: resultLogin.data.token,
          fingerprint: resultLogin.data.fingerprint || "",
        });
        if (resultProtected.data.status === "Success") {
          router.push(getRedirectUrl(resultLogin.data.type_user));
        }
      }
    } catch (error) {
      notify(error);
    }
  };

  const TenantID = process.env.NEXT_PUBLIC_TENANT_ID_AZURE;
  const ClientID = process.env.NEXT_PUBLIC_CLIENT_ID_AZURE;
  const redirectURI = encodeURI(
    process.env.NEXT_PUBLIC_REDIRECT_URL_AZURE || ""
  );
  const URLAuthorization = `https://login.microsoftonline.com/${TenantID}/oauth2/v2.0/authorize?client_id=${ClientID}&response_type=code&redirect_uri=${redirectURI}&response_mode=query&scope=offline_access%20user.read%20mail.read&state=12345`;

  useEffect(() => {
    async function fetchLoginSSo() {
      // console.log("router", router);
      const code = searchParams.get("code");
      const stateUuid = uuidv4();
      // const state = searchParams.get("state");
      const sessionState = searchParams.get("session_state");
      if (code) {
        // Handle the code here
        const fingerprint = localStorage.getItem("fingerprint");
        const callbackURI = encodeURI(
          `/api/auth/loginSSO/callback?code=${code}&state=${stateUuid}&session_state=${sessionState}&fingerprint=${fingerprint}`
        );
        const response = await axiosInstanceAuth
          .get<LoginResponse>(callbackURI)
          .then(async (response) => {
            const {
              token,
              user_id,
              email,
              name,
              company_name,
              company_date,
              division_permission,
              role,
              role_name,
              type_user,
              vendor_id,
              exp,
              status_partner,
              sap_vendor_code,
              fingerprint,
              division_name,
            } = response.data.data;
            setToken(token);
            localStorage.setItem("fingerprint", fingerprint ?? "");
            setUser({
              id: user_id,
              name: name,
              email: email,
              company_name: company_name,
              company_date: company_date,
              division_permission: division_permission,
              role: role,
              role_name: role_name,
              type_user: type_user,
              vendor_id: vendor_id,
              exp: exp,
              status_partner: status_partner,
              sap_vendor_code: sap_vendor_code,
              division_name: division_name,
            });
            const resultProtected =
              await protectedFingerPrintMutation.mutateAsync({
                token: token,
                fingerprint: fingerprint || "",
              });
            if (resultProtected.data.status === "Success") {
              router.push(getRedirectUrl(type_user));
            }
            return response.data;
          })
          .catch((err) => {
            notify("Login failed, please try again.");
          });
      }
    }
    fetchLoginSSo();
  }, [router]);

  const LoginByVendor = () => {
    return (
      <>
        <FormHelper validationSchema={validationSchema} onSubmit={DoLogin}>
          {({ Field }) => (
            <div>
              <Field.Text
                data-testid="auth-login"
                label="Email"
                placeholder="Email"
                name="email"
                required
              />
              <Field.Password
                data-testid="auth-password"
                label="Password"
                placeholder="Password"
                name="password"
                required
              />
              <span className="text-tripatra-9 mt-2 font-semibold">
                <Link href="/auth/forgot-password">{t("forgotPassword")}</Link>
              </span>

              <Button
                type="submit"
                className="bg-tripatra-8 w-full mt-4 hover:bg-tripatra-9 text-white font-bold py-1 px-4 rounded"
                loading={loginMutation.isLoading}
              >
                {t("form.signIn").toUpperCase()}
              </Button>

              <div className="flex">
                <div className="flex-grow">
                  <span className="text-tripatra-9 flex gap-1 mt-3 font-semibold flex-grow">
                    <IconLogin2 size={25} stroke={1.8} />
                    <Link href="/auth/register">{t("registrationHere")}</Link>
                  </span>
                  <span className="text-tripatra-9 flex gap-1 mt-3 font-semibold flex-grow">
                    <IconNotebook size={25} stroke={1.8} />
                    <Link href="/user-guide">{t("userGuide")}</Link>
                  </span>
                  <span className="text-tripatra-9 flex gap-1 mt-3 font-semibold flex-grow">
                    <IconHelp size={25} stroke={1.8} />
                    <Link href="/faq">{t("faq")}</Link>
                  </span>
                </div>
              </div>
            </div>
          )}
        </FormHelper>
      </>
    );
  };

  const LoginByInternal = () => {
    return (
      <>
        <FormHelper validationSchema={validationSchema} onSubmit={DoLogin}>
          {({ Field }) => (
            <div>
              <Field.Text
                data-testid="auth-login"
                label="Email"
                placeholder="Email"
                name="email"
                required
              />
              <Field.Password
                data-testid="auth-password"
                label="Password"
                placeholder="Password"
                name="password"
                required
              />

              <Button
                type="submit"
                className="bg-tripatra-8 w-full mt-4 hover:bg-tripatra-9 text-white font-bold py-1 px-4 rounded"
                loading={loginMutation.isLoading}
              >
                {t("form.signIn").toUpperCase()}
              </Button>

              <Link href={URLAuthorization} passHref>
                <Button
                  className="bg-tripatra-8 mt-3 w-full"
                  leftSection={
                    <img
                      src="https://upload.wikimedia.org/wikipedia/commons/4/44/Microsoft_logo.svg"
                      alt="Microsoft Logo"
                      style={{ width: 20, height: 20 }}
                    />
                  }
                >
                  {t("CONTINUEWITHTRIPATRAACCOUNT")}
                </Button>
              </Link>

              <div className="flex">
                <span className="text-tripatra-9 mt-4 font-semibold flex-grow">
                  <Link href="/user-guide">{t("userGuide")}</Link>
                </span>
              </div>
            </div>
          )}
        </FormHelper>
      </>
    );
  };

  return (
    <div>
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          <Tabs.Tab value="vendor">
            <Text size="lg" fw={520}>
              {t("partner")}
            </Text>
          </Tabs.Tab>
          <Tabs.Tab value="internal">
            <Text size="lg" fw={520}>
              Tripatra
            </Text>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel pt={10} value="vendor">
          <LoginByVendor />
        </Tabs.Panel>
        <Tabs.Panel pt={10} value="internal">
          <LoginByInternal />
        </Tabs.Panel>
      </Tabs>
    </div>
  );
};
